import { useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SelectField,
  TextField,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useIsTrialAccount } from '@/modules/account'
import { useFolder, useFolders, useLoadingFolders } from '@/modules/folders'
import {
  type Project,
  useProjectsMutating,
  useProjectsMutations,
} from '@/modules/projects'
import { useDialogActions, useDialogIsOpen, useForm } from '@/stores'
import { getProjectPath } from '@/utils'

const dialogId = 'create-project'

export const CreateProjectDialog = () => {
  const { successToast, errorToast } = useToast()
  const navigate = useNavigate()
  const open = useDialogIsOpen(dialogId)
  const { closeDialog } = useDialogActions()

  const { data, errors, setFieldError, setFieldValue } = useForm()

  const { createProject } = useProjectsMutations()
  const folder = useFolder()
  const folders = useFolders()
  const isLoadingFolders = useLoadingFolders()
  const isTrial = useIsTrialAccount()
  const isCreating = useProjectsMutating()

  useEffect(() => {
    setFieldValue('folderId', folder?.id)
  }, [folder])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const { name, folderId } = data as Partial<Project>

    if (!name || name.trim().length === 0) {
      setFieldError('name', 'Name is required')
      return
    }

    createProject({ name, folder_id: folderId })
      .then((project) => {
        closeDialog(true)
        successToast('Created project successfully')
        navigate({ to: getProjectPath(project) })
      })
      .catch((error) => errorToast('Failed to create project', error))
  }
  return (
    <Dialog open={open} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new project</DialogTitle>
          <DialogDescription>
            Add a new project to your workspace.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit}>
          <div className="py-4 space-y-4">
            <div className="space-y-6">
              <TextField
                label="Project Name"
                id="project-name"
                placeholder="Enter project name"
                value={data?.name as string}
                error={errors?.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFieldValue('name', e.target.value)
                }
                required
                autoFocus
              />
              {!folder && !isTrial && (
                <SelectField
                  label="Folder"
                  id="folder-name"
                  placeholder="Select a folder"
                  value={data?.folderId as string}
                  onValueChange={(value: string) => {
                    setFieldValue('folderId', value)
                  }}
                  isLoading={isLoadingFolders}
                  options={[
                    ...folders.map((folder) => ({
                      label: folder.name,
                      value: `${folder.id}`,
                    })),
                  ]}
                />
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button theme="outline" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button type="submit" isLoading={isCreating}>
              Create Project
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

CreateProjectDialog.id = dialogId
